import type { Employee } from './employee.type.ts';
import type { PhoneVerificationState } from './phone-number.type.ts';
export interface EmployeeExtension extends Employee {}
export class EmployeeExtension {
  // ── Computed Properties ──
  get fullName() {
    return [this.firstName, this.lastName].join(' ');
  }

  get primaryPhoneNumber() {
    return this.phoneNumbers.find((e) => e.phoneNumberType === 'MOBILE') || this.phoneNumbers[0];
  }

  /**
   * The verification state of the employee's active mobile number, or `none` when
   * there isn't one.
   *
   * Deliberately not built on `primaryPhoneNumber`, which falls back to any phone
   * type: a user whose only row is HOME/WORK has no number we can text or match a
   * caller against, so their answer is `none`, not that row's state. Reads the
   * server-maintained `verificationState` rather than deriving from `isVerified`.
   */
  get phoneVerificationState(): PhoneVerificationState {
    const mobile = this.phoneNumbers.find(
      (phone) => phone.phoneNumberType === 'MOBILE' && phone.isActive
    );

    return mobile?.verificationState ?? 'none';
  }

  get needsToLinkLoans() {
    return !!this.person?.needsToLinkLoans;
  }

  get needsToAnswerWhyNoLinkedLoans() {
    return !!this.person?.needsToAnswerWhyNoLinkedLoans;
  }

  get secureEnrollmentInProgress() {
    return !!this.matchParticipant?.isEnrolling;
  }

  get secureEnrollmentComplete() {
    return !!this.matchParticipant?.isEnrolled;
  }

  get secureBenefitStartDate() {
    return this.matchParticipant?.matchPlan?.benefitStartDate;
  }

  get customAttributesGroupedByPrimitive() {
    const flat: Record<string, unknown> = {};
    const nested: Record<string, unknown> = {};
    const obj: Record<string, unknown> = this.customAttributes;

    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        nested[key] = obj[key];
      } else {
        flat[key] = obj[key];
      }
    }
    return { flat, nested };
  }

  get isTermVerified() {
    return (this.tags || []).some((tag) => tag.name === 'Term Verified');
  }

  get approverPslfGroupIds(): string[] {
    return (this.jurisdictiveRelationships || [])
      .filter((jr) => jr.relationshipType === 'PSLF_GROUP_APPROVER')
      .map((jr) => jr.governableId);
  }

  get hasDependents() {
    return !!this.dependents.length;
  }
}

const Registration = {
  kind: 'object',
  name: 'employee',
  features: EmployeeExtension,
};
export default Registration;
